import React, { useEffect, useState } from 'react';
import './ContactMessagesComponent.css';
import { BottomNav } from './BottomNav';
import {
    DeleteAdminEmail,
    GetAdminEmailById,
    GetAdminEmails,
    RespondAdminEmail,
    UpdateAdminEmailReadStatus,
} from '../Backend/ApiCalls';

const formatDate = (value) => {
    if (!value) {
        return 'Sin fecha';
    }

    return new Date(value).toLocaleString('es-ES', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
};

export const ContactMessagesComponent = ({ I18n, user, handleNotification }) => {
    const [messages, setMessages] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [responseBody, setResponseBody] = useState('');
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUpdatingRead, setIsUpdatingRead] = useState(false);

    const refreshMessages = async () => {
        const { response, data } = await GetAdminEmails(25);

        if (!response.ok) {
            throw new Error(data?.Error || 'No se pudieron cargar los correos.');
        }

        const nextMessages = Array.isArray(data) ? data : [];
        setMessages(nextMessages);
        return nextMessages;
    };

    const updateMessageInList = (id, updater) => {
        setMessages((current) =>
            current.map((message) => (
                message.id === id ? { ...message, ...updater(message) } : message
            ))
        );
    };

    useEffect(() => {
        let cancelled = false;

        const loadInitialMessages = async () => {
            setIsLoadingList(true);

            try {
                const { response, data } = await GetAdminEmails(25);

                if (!response.ok) {
                    throw new Error(data?.Error || 'No se pudieron cargar los correos.');
                }

                if (cancelled) {
                    return;
                }

                setMessages(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error loading emails:', error);
                if (!cancelled) {
                    handleNotification?.(error.message || 'No se pudieron cargar los correos.', 'error');
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingList(false);
                }
            }
        };

        loadInitialMessages();

        return () => {
            cancelled = true;
        };
    }, [handleNotification]);

    const openMessage = async (messageId) => {
        if (selectedId === messageId) {
            setSelectedId('');
            setSelectedMessage(null);
            setResponseBody('');
            return;
        }

        setSelectedId(messageId);
        setIsLoadingDetail(true);

        try {
            const { response, data } = await GetAdminEmailById(messageId);

            if (!response.ok) {
                throw new Error(data?.Error || 'No se pudo leer el correo.');
            }

            setSelectedMessage(data);
            setResponseBody('');

            if (!data.isRead) {
                await UpdateAdminEmailReadStatus(messageId, true);
                setSelectedMessage((current) => current ? { ...current, isRead: true } : current);
                updateMessageInList(messageId, () => ({ isRead: true }));
            }
        } catch (error) {
            console.error('Error loading email detail:', error);
            handleNotification?.(error.message || 'No se pudo leer el correo.', 'error');
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const handleToggleRead = async () => {
        if (!selectedId || !selectedMessage) {
            return;
        }

        const nextReadValue = !selectedMessage.isRead;
        setIsUpdatingRead(true);

        try {
            const { response, data } = await UpdateAdminEmailReadStatus(selectedId, nextReadValue);

            if (!response.ok) {
                throw new Error(data?.Error || 'No se pudo actualizar el estado del correo.');
            }

            setSelectedMessage((current) => current ? { ...current, isRead: nextReadValue } : current);
            updateMessageInList(selectedId, () => ({ isRead: nextReadValue }));
            handleNotification?.(
                nextReadValue ? 'Correo marcado como leído.' : 'Correo marcado como no leído.',
                'success'
            );
        } catch (error) {
            console.error('Error updating read status:', error);
            handleNotification?.(error.message || 'No se pudo actualizar el estado del correo.', 'error');
        } finally {
            setIsUpdatingRead(false);
        }
    };

    const handleDeleteMessage = async () => {
        if (!selectedId || !selectedMessage) {
            return;
        }

        setIsDeleting(true);

        try {
            const { response, data } = await DeleteAdminEmail(selectedId);

            if (!response.ok) {
                throw new Error(data?.Error || 'No se pudo eliminar el correo.');
            }

            setMessages((current) => current.filter((message) => message.id !== selectedId));
            setSelectedId('');
            setSelectedMessage(null);
            setResponseBody('');
            handleNotification?.('Correo eliminado correctamente.', 'success');
        } catch (error) {
            console.error('Error deleting email:', error);
            handleNotification?.(error.message || 'No se pudo eliminar el correo.', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleQuickDelete = async (event, messageId) => {
        event.stopPropagation();

        try {
            const { response, data } = await DeleteAdminEmail(messageId);

            if (!response.ok) {
                throw new Error(data?.Error || 'No se pudo eliminar el correo.');
            }

            setMessages((current) => current.filter((message) => message.id !== messageId));

            if (selectedId === messageId) {
                setSelectedId('');
                setSelectedMessage(null);
                setResponseBody('');
            }

            handleNotification?.('Correo eliminado correctamente.', 'success');
        } catch (error) {
            console.error('Error deleting email:', error);
            handleNotification?.(error.message || 'No se pudo eliminar el correo.', 'error');
        }
    };

    const handleSendResponse = async () => {
        if (!selectedMessage) {
            return;
        }

        if (!responseBody.trim()) {
            handleNotification?.('La respuesta no puede estar vacía.', 'error');
            return;
        }

        setIsSending(true);

        try {
            const references = [
                ...(selectedMessage.references || []),
                selectedMessage.messageId,
            ].filter(Boolean);

            const recipientEmail = selectedMessage.replyToEmail || selectedMessage.fromEmail;

            const { response, data } = await RespondAdminEmail({
                to: recipientEmail,
                subject: selectedMessage.subject,
                message: responseBody,
                originalMessageId: selectedMessage.messageId,
                originalEmailId: selectedId,
                references,
            });

            if (!response.ok) {
                throw new Error(data?.Error || 'No se pudo enviar la respuesta.');
            }

            setResponseBody('');
            setSelectedMessage((current) => current ? { ...current, isAnswered: true, isRead: true } : current);
            updateMessageInList(selectedId, () => ({ isAnswered: true, isRead: true }));
            handleNotification?.('Respuesta enviada correctamente.', 'success');
            await refreshMessages();
        } catch (error) {
            console.error('Error sending response:', error);
            handleNotification?.(error.message || 'No se pudo enviar la respuesta.', 'error');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className='admin-messages-container'>
            <BottomNav I18n={I18n} user={user} />

            <h1 className='titulo-page'>Mensajes Recibidos</h1>

            <div className='messages-list messages-list-inline'>
                {isLoadingList ? <p>Cargando correos...</p> : null}

                {!isLoadingList && messages.length === 0 ? (
                    <p>No hay correos en el buzón.</p>
                ) : null}

                {messages.map((message) => {
                    const isOpen = selectedId === message.id;
                    const detail = isOpen ? selectedMessage : null;
                    const isThreadMuted = Boolean(message.isAnswered || message.isReply);

                    return (
                        <div
                            key={message.id}
                            className={`message-card ${message.isRead ? 'read' : 'unread'} ${isThreadMuted ? 'thread-muted' : ''} ${isOpen ? 'active' : ''}`}
                        >
                            <div className='message-item-row'>
                                <button
                                    type="button"
                                    className='message-item'
                                    onClick={() => openMessage(message.id)}
                                >
                                    <div className='message-info'>
                                        <div className='message-headline'>
                                            <h2>{message.from || 'Remitente desconocido'}</h2>
                                            <div className='message-badges'>
                                                <span className={`message-badge ${message.isRead ? 'read' : 'unread'}`}>
                                                    {message.isRead ? 'Leído' : 'No leído'}
                                                </span>
                                                {message.isAnswered ? (
                                                    <span className='message-badge answered'>Respondido</span>
                                                ) : null}
                                                {message.isReply ? (
                                                    <span className='message-badge reply'>Respuesta</span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <p>{message.subject || 'Sin asunto'}</p>
                                        <small>{formatDate(message.date)}</small>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    className='quick-delete-button'
                                    onClick={(event) => handleQuickDelete(event, message.id)}
                                    title='Eliminar correo'
                                    aria-label='Eliminar correo'
                                >
                                    Eliminar
                                </button>
                            </div>

                            {isOpen ? (
                                <div className='message-response inline-response'>
                                    {isLoadingDetail ? <p>Cargando detalle...</p> : null}

                                    {detail && !isLoadingDetail ? (
                                        <>
                                            <h2>{detail.subject || message.subject || 'Sin asunto'}</h2>
                                            <p><strong>De:</strong> {detail.from || message.from || 'Sin remitente'}</p>
                                            <p><strong>Email:</strong> {detail.fromEmail || 'No disponible'}</p>
                                            <p><strong>Responder a:</strong> {detail.replyToEmail || detail.fromEmail || 'No disponible'}</p>
                                            <p><strong>Fecha:</strong> {formatDate(detail.date || message.date)}</p>

                                            <div className='message-actions'>
                                                <button type="button" onClick={handleToggleRead} disabled={isUpdatingRead}>
                                                    {isUpdatingRead
                                                        ? 'Guardando...'
                                                        : detail.isRead ? 'Marcar como no leído' : 'Marcar como leído'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className='danger-button'
                                                    onClick={handleDeleteMessage}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? 'Eliminando...' : 'Eliminar'}
                                                </button>
                                            </div>

                                            <div className='message-detail-box'>
                                                <pre>{detail.text || 'Este correo no contiene texto plano.'}</pre>
                                            </div>

                                            <h3>Responder</h3>
                                            <textarea
                                                value={responseBody}
                                                onChange={(event) => setResponseBody(event.target.value)}
                                                placeholder="Escribe tu respuesta..."
                                            />

                                            <div className='response-buttons'>
                                                <button type="button" onClick={handleSendResponse} disabled={isSending}>
                                                    {isSending ? 'Enviando...' : 'Enviar respuesta'}
                                                </button>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
